/**
 * Conversation Service — chat-first dashboard persistence.
 *
 * User-scoped group conversations with specialist agents (Firestore-backed via
 * the apiGateway). Replaces the earlier localStorage stopgap.
 */
import { callFunction } from '../utils/apiClient';

export interface ConversationMember {
  id: string;
  name: string;
  persona: string;
  avatar_icon: string;
  /** Owner-set group-specific display name (overrides `name` in this chat). */
  alias?: string | null;
  /** Storage path of an owner-generated avatar image (overrides `avatar_icon`). */
  avatar_path?: string | null;
  /** Drive file id of the avatar when the chat's media lives in the owner's cloud drive. */
  avatar_drive_file_id?: string | null;
  profile_id?: string | null;
  system_prompt?: string | null;
  model_tier?: string | null;
  temperature?: number | null;
}

export interface HumanMember {
  uid: string;
  name: string;
  role: 'owner' | 'member';
}

export interface ConversationSummary {
  id: string;
  user_id: string;
  title: string;
  slug?: string;
  members: ConversationMember[];
  human_members?: HumanMember[];
  participant_uids?: string[];
  storage_location?: 'cloud' | 'drive';
  logo_url?: string | null;
  language?: string | null;
  agents_native_language?: boolean;
  last_message: string | null;
  message_count: number;
  created_at: string;
  updated_at: string;
}

/** A reference file the user attached to an enquiry (uploaded for research/ETL). */
export interface MessageAttachment {
  name: string;
  mime: string;
  kind: 'document' | 'image' | 'data' | 'audio' | 'video';
  size: number;
  /** Extracted text the agents can analyse (client-side for text/CSV/MD). */
  text_excerpt?: string | null;
  /** Storage path once binaries are uploaded (server-side extraction later). */
  storage_path?: string | null;
  /** Drive file id when this chat's media lives in the owner's Google Drive. */
  drive_file_id?: string | null;
  /** Generated cover/poster image (storage path) for a song or video — shown as album art. */
  poster_path?: string | null;
  /** The song's lyrics (persisted at generation) — shown in the expanded player. Empty for instrumentals. */
  lyrics?: string | null;
}

export interface ConversationMessage {
  id: string;
  author: string;
  author_name: string;
  avatar_icon: string | null;
  content: string;
  spec: unknown | null;
  attachments?: MessageAttachment[];
  created_at: string;
}

export const listConversations = () =>
  callFunction<{ conversations: ConversationSummary[] }>('listConversations', {}).then((r) => r.conversations || []);

export type ProactiveLevel = 'none' | 'occasional' | 'collaborative' | 'vibrant';

export const createConversation = (members: ConversationMember[], title: string, proactiveLevel: ProactiveLevel = 'none') =>
  callFunction<ConversationSummary>('createConversation', { members, title, proactiveLevel });

// ── Group notes/requests (A2) — the owner jots a request; once a day the best-matched crew agent picks it up ──
export interface GroupNote {
  id: string; text: string; status: 'open' | 'picked' | 'done';
  created_at: string; picked_by_agent?: string | null; finding_message_id?: string | null;
}
export const listGroupNotes = (conversationId: string) =>
  callFunction<{ notes: GroupNote[] }>('listGroupNotes', { conversationId }).then((r) => r.notes || []);
export const addGroupNote = (conversationId: string, text: string) =>
  callFunction<{ id: string; notes: GroupNote[] }>('addGroupNote', { conversationId, text }).then((r) => r.notes || []);
export const resolveGroupNote = (conversationId: string, noteId: string, remove = false) =>
  callFunction<{ notes: GroupNote[] }>('resolveGroupNote', { conversationId, noteId, remove }).then((r) => r.notes || []);

export const getConversationMessages = (conversationId: string) =>
  callFunction<{ messages: ConversationMessage[] }>('getConversationMessages', { conversationId }).then((r) => r.messages || []);

export const deleteConversation = (conversationId: string) =>
  callFunction<{ deleted: boolean }>('deleteConversation', { conversationId });

/** Archive a chat (owner) — drops it from the active list onto the Archived page. */
export const archiveConversation = (conversationId: string, archived = true) =>
  callFunction<{ conversation: ConversationSummary }>('archiveConversation', { conversationId, archived });

/** The owner's archived chats (for the Archived section in account settings). */
export const listArchivedConversations = () =>
  callFunction<{ conversations: ConversationSummary[] }>('listArchivedConversations', {}).then((r) => r.conversations || []);

export interface MessageReaction { emoji: string; by: string; name: string }

/** Toggle the caller's emoji reaction on a message (server validates the emoji allowlist). */
/** A Link (rapport) level-up to celebrate in the UI when an interaction crosses a level. */
export interface LinkUp { agentId: string; name: string; level: number }

export const toggleConversationReaction = (conversationId: string, messageId: string, emoji: string) =>
  callFunction<{ messageId: string; reactions: MessageReaction[]; linkUp?: LinkUp | null }>('toggleConversationReaction', { conversationId, messageId, emoji });

/** Owner renames the group → new title + a fresh memorable URL slug. */
export const renameConversation = (conversationId: string, title: string) =>
  callFunction<ConversationSummary>('renameConversation', { conversationId, title });

/** Owner bookmarks/unbookmarks a message (pin for later). */
export const bookmarkConversationMessage = (conversationId: string, messageId: string, bookmarked: boolean) =>
  callFunction<{ messageId: string; bookmarked: boolean }>('bookmarkConversationMessage', { conversationId, messageId, bookmarked });

/** Owner deletes a message from the chat. */
export const deleteConversationMessage = (conversationId: string, messageId: string) =>
  callFunction<{ messageId: string; deleted: boolean }>('deleteConversationMessage', { conversationId, messageId });

/** Optional generation preferences the user picks before sending (media dimension, song singer/style). */
export interface SendMessageOptions {
  /** Aspect ratio for image/video generation, e.g. '9:16' | '1:1' | '4:5' | '16:9'. */
  mediaAspect?: string;
  /** Output size/resolution for image/video: 'standard' | 'hd'. */
  mediaSize?: string;
  /** Natural-language singer/voice/genre descriptor prepended to a music prompt. */
  musicStyle?: string;
  /** Music TYPE the user picked → routes the model: 'music' (vocals) | 'music_instrumental' | 'sfx'. */
  musicKind?: string;
  /** Style-preset key (prompt factory) — composed server-side into the image/video prompt. */
  mediaStyle?: string;
  /** Camera-move key (video only) — a named cinematographer move woven server-side into the video prompt. */
  cameraMove?: string;
  /** Song length in seconds (music only) — scales the credit cost (1 block = 30 s). */
  durationSec?: number;
  /** Spend-confirm resume: the user accepted the cost → skip the confirm gate and generate. */
  confirmed?: boolean;
  /** With a confirm resume, don't re-post the user message (it's already in the thread). */
  silent?: boolean;
  /** Human-in-the-loop preview: the approved (possibly edited) generation prompt on a confirm resume. */
  previewPrompt?: string;
  /** Human-in-the-loop preview: the approved (possibly edited) song lyrics on a confirm resume. */
  previewLyrics?: string;
}

export const sendConversationMessage = (
  conversationId: string,
  message: string,
  targetMemberIds?: string[],
  attachments?: MessageAttachment[],
  clientMsgId?: string,
  options?: SendMessageOptions
) =>
  callFunction<{ userMessage: ConversationMessage; replies: ConversationMessage[]; linkUps?: LinkUp[]; busy?: boolean }>(
    'sendConversationMessage',
    {
      conversationId, message, targetMemberIds, attachments, client_msg_id: clientMsgId,
      mediaAspect: options?.mediaAspect, mediaSize: options?.mediaSize, musicStyle: options?.musicStyle, musicKind: options?.musicKind, mediaStyle: options?.mediaStyle, cameraMove: options?.cameraMove,
      durationSec: options?.durationSec,
      confirmed: options?.confirmed, silent: options?.silent,
      previewPrompt: options?.previewPrompt, previewLyrics: options?.previewLyrics,
    },
    // Media generation (esp. video — 2-5 min) can take a while; the backend apiGateway caps at 300s
    // (fal fetch 280s), so the client must wait a little beyond that before giving up. Replies also
    // stream in live via onSnapshot, so this only governs the final HTTP ack.
    { timeout: 310000 },
  );

/** Toggle the spend-confirm prompt ("don't ask again" → enabled:false). */
export const setSpendConfirm = (enabled: boolean) =>
  callFunction<{ confirm_spend: boolean }>('setSpendConfirm', { enabled });

/** Read a message aloud in the agent's voice. Cached on the message, so replay is free (no charge). TTS through
 *  the fal voice model can queue past the 60s default, so allow a longer deadline (the backend fal call caps
 *  at ~280s and charges only on success) — otherwise the client aborts with "taking longer than usual" while
 *  the voice is still generating. */
export const speakConversationMessage = (conversationId: string, messageId: string) =>
  callFunction<{ voicePath: string; mime: string; cached: boolean }>(
    'speakConversationMessage', { conversationId, messageId }, { timeout: 180000 },
  );

// ── Member & settings management (owner) ──────────────────────────────────────

export const addConversationMember = (conversationId: string, member: ConversationMember) =>
  callFunction<ConversationSummary>('addConversationMember', { conversationId, member });

/** Recommendation action 1 — add a recommended CATALOG agent (by profile id) to the group. */
export const addSuggestedAgent = (conversationId: string, agentId: string) =>
  callFunction<ConversationSummary>('addSuggestedAgent', { conversationId, agentId });

/** Recommendation action 2 — "ask once": pull a non-member specialist in for a single guest
 *  reply (streams back via the live snapshot). LLM call, so allow the longer timeout. */
export const consultAgent = (conversationId: string, agentId: string, message?: string) =>
  callFunction<{ reply: ConversationMessage }>('consultAgent', { conversationId, agentId, message }, { timeout: 130000 });

export const removeConversationMember = (conversationId: string, memberId: string) =>
  callFunction<ConversationSummary>('removeConversationMember', { conversationId, memberId });

/** Owner edits a specialist's group-specific alias and/or clears its avatar. */
export const updateConversationMember = (
  conversationId: string,
  memberId: string,
  patch: { alias?: string | null; avatar_path?: string | null; avatar_drive_file_id?: string | null; context?: string | null }
) => callFunction<ConversationSummary>('updateConversationMember', { conversationId, memberId, ...patch });

/** Owner generates a styled avatar for a specialist (charges credits). `style` picks a preset look
 *  (default cyberpunk); `prompt` is the owner's optional free-text direction. */
export const generateMemberAvatar = (conversationId: string, memberId: string, prompt?: string, style?: string) =>
  callFunction<{ conversation: ConversationSummary; avatar_path: string }>(
    'generateMemberAvatar',
    { conversationId, memberId, prompt, style }
  );

/** Avatar style presets (key + label) — mirror of the server's avatarStyleChips(); the prompt text
 *  stays server-side. Keep keys in sync with functions/src/agents/avatar/memberAvatar.ts. */
export const AVATAR_STYLE_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'cyberpunk', label: 'Cyberpunk' },
  { key: 'fantasy', label: 'Medieval Fantasy' },
  { key: 'idol', label: 'Asian Idol' },
  { key: 'photoreal', label: 'Photorealistic' },
  { key: 'pixel', label: 'Pixel Art' },
  { key: 'anime', label: 'Anime' },
  { key: 'comic', label: 'Comic Book' },
];

export const removeConversationParticipant = (conversationId: string, uid: string) =>
  callFunction<ConversationSummary>('removeConversationParticipant', { conversationId, uid });

export const setConversationStorage = (conversationId: string, location: 'cloud' | 'drive') =>
  callFunction<ConversationSummary>('setConversationStorage', { conversationId, location });

export interface StorageStatus {
  storage_location: 'cloud' | 'drive';
  migration_status: 'none' | 'migrating' | 'active' | 'reverting' | 'error';
  drive_folder_id: string | null;
}

/** Owner moves this chat's messages to their Google Drive (Drive becomes the store). */
export const startConversationDriveMigration = (conversationId: string) =>
  callFunction<StorageStatus>('startConversationDriveMigration', { conversationId });

/** Owner returns this chat to Wisetree Cloud (copies messages back from Drive). */
export const revertConversationToCloud = (conversationId: string) =>
  callFunction<StorageStatus>('revertConversationToCloud', { conversationId });

export const getConversationStorageStatus = (conversationId: string) =>
  callFunction<StorageStatus>('getConversationStorageStatus', { conversationId });

export const setConversationLogo = (conversationId: string, logoUrl: string | null) =>
  callFunction<ConversationSummary>('setConversationLogo', { conversationId, logoUrl });

/** The logo looks the owner can pick (key + label). Mirrors the backend LOGO_STYLES. */
export const LOGO_STYLES: { key: string; label: string }[] = [
  { key: 'minimal', label: 'Minimal mark' },
  { key: 'emblem', label: 'Emblem / badge' },
  { key: 'landscape', label: 'Landscape' },
  { key: '3d', label: '3D render' },
  { key: 'mascot', label: 'Mascot' },
  { key: 'lettermark', label: 'Monogram' },
  { key: 'illustrated', label: 'Illustrated crest' },
];

/** Owner generates a chat logo from a prompt + a STYLE (charges credits). Returns the image as base64
 *  for the client to crop + upload. Throws an out-of-credits error when the wallet is short. */
export const generateConversationLogo = (conversationId: string, prompt?: string, style?: string) =>
  callFunction<{ imageBase64: string; mime: string }>('generateConversationLogo', { conversationId, prompt, style });

/** Proactive autonomy — the owner-settable slice (server manages counters/cooldowns). */
export interface ConversationAutonomy {
  level: ProactiveLevel;
  ambient: 'off' | 'light' | 'standard';
  discussion: 'off' | 'lively' | 'active';
  daily_sp_budget: number;
  daily_mp_budget: number;
  muted: boolean;
}

export const getConversationAutonomy = (conversationId: string) =>
  callFunction<ConversationAutonomy>('getConversationAutonomy', { conversationId });

export const setConversationAutonomy = (conversationId: string, autonomy: Partial<ConversationAutonomy>) =>
  callFunction<ConversationAutonomy>('setConversationAutonomy', { conversationId, autonomy });

/** Owner sparks ONE proactive beat now (on demand) — the group comes alive without waiting for the sweep. */
export const runConversationBeat = (conversationId: string) =>
  callFunction<{ emitted: number; kind: string }>('runConversationBeat', { conversationId });

/** The group's main language the user can pick — agents reply in it by default (major-economy set). */
export const CHAT_LANGUAGES: { code: string; label: string }[] = [
  { code: '', label: 'Match me (default)' },
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文 (Mandarin)' },
  { code: 'es', label: 'Español (España)' },
  { code: 'ja', label: '日本語' },
  { code: 'fr', label: 'Français' },
  { code: 'ko', label: '한국어' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
];

/** Set (or clear with '') the group's main language. */
export const setConversationLanguage = (conversationId: string, language: string) =>
  callFunction<{ language: string | null }>('setConversationLanguage', { conversationId, language });

/** Toggle whether each agent replies in its own native language (group setting). */
export const setConversationAgentsNativeLanguage = (conversationId: string, on: boolean) =>
  callFunction<{ agents_native_language: boolean }>('setConversationAgentsNativeLanguage', { conversationId, on });

/** The signed-in user's Link (rapport) level 1-20 with a specific agent (catalog profile_id or custom_agent_id). */
export const getAgentLink = (agentId: string) =>
  callFunction<{ level: number; points: number }>('getAgentLink', { agentId });

/** Translate a message to English (the in-bubble "See translation"). Cached server-side; free. */
export const translateConversationMessage = (conversationId: string, messageId: string) =>
  callFunction<{ translation: string; spec?: unknown; cached: boolean }>('translateConversationMessage', { conversationId, messageId });

/** Thumbs up/down on an agent-produced artifact (system feedback; prompt is PII-masked server-side). */
export const submitContentFeedback = (p: {
  conversationId: string; messageId: string; feedback: 'up' | 'down';
  contentKind: 'image' | 'document' | 'media' | 'artifact'; prompt?: string;
}) => callFunction<{ saved: boolean; feedback: 'up' | 'down' }>('submitContentFeedback', p);
